import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Phone, Mail, Lock, User, KeyRound } from 'lucide-react';
import { getManagers, createLoginSession, loginManager, requestUnlockEmail } from '../api';
import { toast } from '../hooks/use-toast';

const Login = () => {
  const navigate = useNavigate();
  const [managers, setManagers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetUsername, setResetUsername] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [lockedUser, setLockedUser] = useState('');
  const [isSendingUnlock, setIsSendingUnlock] = useState(false);

  const handleUnlockEmail = async () => {
    setIsSendingUnlock(true);
    try {
      const res = await requestUnlockEmail(lockedUser);
      toast({ title: 'נשלח', description: res.message || 'קישור לשחרור נשלח למייל של המנהל', duration: 8000 });
    } catch (error) {
      toast({ title: 'שגיאה', description: error.response?.data?.detail || 'לא ניתן לשלוח מייל', variant: 'destructive' });
    } finally {
      setIsSendingUnlock(false);
    }
  };

  useEffect(() => {
    loadManagers();
    const unlock = new URLSearchParams(window.location.search).get('unlock');
    if (unlock) {
      window.history.replaceState(null, '', '/');
      const msgs = {
        ok: { title: 'החשבון שוחרר', description: 'הנעילה הוסרה. אפשר להתחבר עכשיו.' },
        expired: { title: 'הקישור פג תוקף', description: 'בקש קישור חדש לשחרור נעילה', variant: 'destructive' },
        invalid: { title: 'קישור לא תקין', description: 'הקישור כבר נוצל או אינו תקין', variant: 'destructive' },
      };
      // Defer: <Toaster/> subscribes in its own effect, which runs after this one on first mount
      setTimeout(() => toast(msgs[unlock] || msgs.invalid), 0);
    }
    const loginError = sessionStorage.getItem('loginError');
    if (loginError) {
      sessionStorage.removeItem('loginError');
      setTimeout(() => toast({ title: 'שגיאה בהתחברות', description: loginError, variant: 'destructive' }), 0);
    }
    // Check if already logged in
    const loggedIn = localStorage.getItem('managerLoggedIn');
    if (loggedIn === 'true') {
      navigate('/home');
    }
  }, [navigate]);

  const loadManagers = async () => {
    try {
      const data = await getManagers();
      setManagers(data);
    } catch (error) {
      console.error('Error loading managers:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.password) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא שם משתמש וסיסמה',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    try {
      // Server-side login verification with lockout protection
      const result = await loginManager(formData.name, formData.password);

      if (result.success) {
        // Login successful
        localStorage.setItem('managerLoggedIn', 'true');
        localStorage.setItem('managerName', result.name);
        if (result.token) {
          localStorage.setItem('authToken', result.token);
        }
        
        // Register login session
        try {
          const sessionData = await createLoginSession(result.name);
          localStorage.setItem('sessionId', sessionData.sessionId);
          
          if (sessionData.suspicious) {
            toast({
              title: 'כניסה ממכשיר חדש!',
              description: `זוהתה כניסה ממכשיר לא מוכר: ${sessionData.deviceInfo} (IP: ${sessionData.ip}). אם זה לא אתה, שנה את הסיסמה מיד.`,
              variant: 'destructive',
              duration: 15000
            });
          }
        } catch (err) {
          console.error('Failed to register session:', err);
        }

        toast({
          title: 'הצלחה!',
          description: result.message || `ברוך הבא ${result.name}`,
        });

        setTimeout(() => {
          navigate('/home');
        }, 500);
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 423) {
        // Account locked
        setLockedUser(formData.name || '');
        toast({
          title: 'חשבון נעול',
          description: detail || 'החשבון ננעל. נסה שוב מאוחר יותר.',
          variant: 'destructive',
          duration: 10000
        });
      } else if (status === 401) {
        toast({
          title: 'שגיאה',
          description: detail || 'שם משתמש או סיסמה שגויים',
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'שגיאה',
          description: 'אירעה שגיאה בהתחברות',
          variant: 'destructive'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInitializeManager = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/managers/initialize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'הצלחה!',
          description: data.message || 'מנהל ראשוני נוצר בהצלחה',
        });
        // Reload managers
        loadManagers();
      } else {
        toast({
          title: 'מידע',
          description: data.message || 'המנהלים כבר קיימים במערכת',
        });
      }
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'אירעה שגיאה ביצירת המנהל',
        variant: 'destructive'
      });
    }
  };

  const handleResetPassword = async () => {
    if (!resetUsername) {
      toast({
        title: 'שגיאה',
        description: 'נא להזין שם משתמש',
        variant: 'destructive'
      });
      return;
    }

    setIsResetting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/managers/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: resetUsername })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'הסיסמה אופסה בהצלחה',
          description: `רמז לסיסמה: ${data.hint}`,
        });
        setShowForgotPassword(false);
        setResetUsername('');
      } else {
        toast({
          title: 'שגיאה',
          description: data.detail || 'לא ניתן לאפס סיסמה',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'אירעה שגיאה באיפוס הסיסמה',
        variant: 'destructive'
      });
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center py-12 px-4" dir="rtl">
      <div className="max-w-4xl w-full">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            אימן שירותי מחשוב
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            התקנת רשתות תקשורת מכירה והתקנת מחשבים נייחים וניידים מצלמות ומעגל סגור ואינטרקומים
          </p>
          
          {/* Contact Info */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center gap-2 text-gray-700">
              <Mail className="w-5 h-5 text-blue-600" />
              <span className="text-lg">aiman@aimanpc.com</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Phone className="w-5 h-5 text-blue-600" />
              <span className="text-lg">0525767555</span>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <Card className="shadow-2xl border-0 max-w-md mx-auto">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <CardTitle className="text-3xl font-bold text-center">כניסה למערכת</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-base font-semibold text-gray-700 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  שם משתמש
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="הזן שם משתמש"
                  className="text-base py-6 border-2 focus:border-blue-500 transition-colors"
                  required
                  autoFocus
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-base font-semibold text-gray-700 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  סיסמה
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="הזן סיסמה"
                  className="text-base py-6 border-2 focus:border-blue-500 transition-colors"
                  required
                />
              </div>

              {/* Login Button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="login-submit-button"
              >
                {isLoading ? 'מתחבר...' : 'התחבר למערכת'}
              </Button>

              {lockedUser && (
                <div className="rounded-lg border-2 border-red-200 bg-red-50 p-3 text-center space-y-2" data-testid="locked-account-box">
                  <p className="text-sm font-semibold text-red-700">החשבון "{lockedUser}" נעול זמנית</p>
                  <Button type="button" onClick={handleUnlockEmail} disabled={isSendingUnlock} variant="outline" className="w-full border-2 border-red-300 text-red-700 hover:bg-red-100" data-testid="unlock-email-btn">
                    <Mail className="w-4 h-4 ml-2" />{isSendingUnlock ? 'שולח...' : 'שחרר נעילה במייל'}
                  </Button>
                </div>
              )}

            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2 border-gray-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-sm text-gray-500">או</span>
              </div>
            </div>

            {/* Google Sign-In Button */}
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <Button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + '/home';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full py-6 text-lg font-semibold bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-300 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-center gap-3"
              data-testid="google-login-button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              התחבר עם Google
            </Button>

            {/* Info Message */}
            {managers.length === 0 && (
              <div className="mt-6 space-y-4">
                <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 text-center mb-3">
                    אין מנהלים במערכת. לחץ על הכפתור למטה ליצירת מנהל ראשוני.
                  </p>
                  <Button
                    onClick={handleInitializeManager}
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                  >
                    צור מנהל ראשוני (aiman)
                  </Button>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800 text-center">
                    אחרי יצירת המנהל הראשוני, השתמש בפרטים שיוצגו בהודעת ההצלחה
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-600">
          <p className="text-sm">© 2025 אימן שירותי מחשוב - כל הזכויות שמורות</p>
        </div>

        {/* Forgot Password Dialog */}
        <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
          <DialogContent className="max-w-sm" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-center">איפוס סיסמה</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-gray-600 text-center">
                הזן את שם המשתמש שלך לאיפוס הסיסמה
              </p>
              <div className="space-y-2">
                <Label htmlFor="reset-username" className="text-sm font-semibold text-gray-700">
                  שם משתמש
                </Label>
                <Input
                  id="reset-username"
                  value={resetUsername}
                  onChange={(e) => setResetUsername(e.target.value)}
                  placeholder="הזן שם משתמש"
                  className="border-2 focus:border-blue-500"
                  data-testid="reset-password-username-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleResetPassword()}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleResetPassword}
                  disabled={isResetting}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                  data-testid="reset-password-submit-button"
                >
                  {isResetting ? 'מאפס...' : 'אפס סיסמה'}
                </Button>
                <Button
                  onClick={() => { setShowForgotPassword(false); setResetUsername(''); }}
                  variant="outline"
                  className="flex-1"
                >
                  ביטול
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Login;
